// What to expect, hotspots (sections/expect-hotspots.liquid), under 990px: the dots on the dog and the row of
// benefit cards under it stay in step.
// - Tapping a dot scrolls its card (aria-controls) to the row's start and marks the dot at once.
// - Swiping the row: the card that is mostly in view (IntersectionObserver on the row) marks its dot.
// - The marked dot has aria-current="true"; the first one is marked in the markup.
// Smooth scrolling only without reduced motion. From 990px the dots and the row are hidden, so nothing fires.
// "Cards on phones: Stage" (the cards in a stay-carousel stage, which doesn't scroll): tapping a dot brings its card
// to the middle through the carousel (goTo, announced), and the carousel's "stay-carousel:change" (a swipe, a tap on
// a card behind, the keys) marks the card's dot.
if (!customElements.get('expect-hotspots-stage')) {
  customElements.define(
    'expect-hotspots-stage',
    class ExpectHotspotsStage extends HTMLElement {
      connectedCallback() {
        this.row = this.querySelector('[data-hotspot-cards]');
        this.dots = [...this.querySelectorAll('[aria-controls]')];
        if (!this.row || !this.dots.length) return;

        this.cards = this.dots.map((dot) => document.getElementById(dot.getAttribute('aria-controls')));
        this.target = null;

        this.carousel = this.row.closest('stay-carousel');
        if (this.carousel) {
          this.dots.forEach((dot, index) =>
            dot.addEventListener('click', () => {
              this.mark(index);
              this.carousel.goTo?.(index, true);
              this.reveal();
            })
          );
          this.carousel.addEventListener('stay-carousel:change', (event) => this.mark(event.detail.index));
          return;
        }

        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index)));
        this.row.addEventListener('focusin', (event) => {
          const index = this.cards.indexOf(event.target);
          if (index > -1 && this.target === null) this.mark(index);
        });

        this.observer = new IntersectionObserver(
          (entries) => {
            if (this.target !== null) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.mark(this.cards.indexOf(entry.target));
            });
          },
          { root: this.row, threshold: 0.6 }
        );
        this.cards.forEach((card) => card && this.observer.observe(card));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      mark(index) {
        if (index < 0) return;
        this.dots.forEach((dot, i) => {
          if (i === index) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      // The row into view when it is partly off the screen
      reveal() {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const rowBox = this.row.getBoundingClientRect();
        if (rowBox.bottom > window.innerHeight || rowBox.top < 0) {
          this.row.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
        }
      }

      // Scroll the card to the row's start (and the row into view); the observer waits until the scroll ends,
      // so the cards passed on the way don't mark their dots
      show(index) {
        const card = this.cards[index];
        if (!card) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mark(index);
        this.target = index;

        const edge = parseFloat(getComputedStyle(this.row).scrollPaddingInlineStart) || 0;
        const left = this.row.scrollLeft + card.getBoundingClientRect().left - this.row.getBoundingClientRect().left - edge;
        this.row.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
        this.reveal();

        clearTimeout(this.release);
        this.release = setTimeout(() => {
          this.target = null;
        }, reduce ? 100 : 900);
      }
    }
  );
}
